import type { Timestamp } from "@um/contracts/firestore";
import type { DatabaseRecord } from "./schema";

/**
 * Retention / win-back contracts.
 *
 * When a subscriber turns off auto-renew (RevenueCat `CANCELLATION` webhook —
 * fires even when the cancel happens in the App Store / Play Store, outside
 * the app), the backend records the event, emails a one-tap "why did you
 * cancel?" survey, and offers a one-time free month so the family can keep
 * exploring while the app improves. See docs/retention.md.
 */

/**
 * Why a family cancelled. Shared by the in-app exit survey, the win-back
 * email survey page, and the admin Feedback grid.
 */
export type CancellationReason =
  | "too_expensive"
  | "child_not_engaged"
  | "missing_features"
  | "bugs"
  | "just_exploring"
  | "other";

export const CANCELLATION_REASONS: CancellationReason[] = [
  "too_expensive",
  "child_not_engaged",
  "missing_features",
  "bugs",
  "just_exploring",
  "other",
];

export const CANCELLATION_REASON_LABELS: Record<CancellationReason, string> = {
  too_expensive: "It costs too much",
  child_not_engaged: "My child didn't engage with it",
  missing_features: "It's missing something we need",
  bugs: "Bugs or technical problems",
  just_exploring: "I was just exploring",
  other: "Something else",
};

export type RetentionOfferStatus =
  /** Offer created and win-back email sent (or sending). */
  | "sent"
  /** Free month granted via promotional entitlement. */
  | "claimed"
  /** Subscriber re-enabled auto-renew (RC `UNCANCELLATION`) before claiming. */
  | "void";

/**
 * One win-back offer, created by the RevenueCat webhook on `CANCELLATION`.
 * Lives at `RetentionOffer/{offerId}` — the unguessable Firestore id doubles
 * as the capability token in the emailed claim link
 * (`{www}/retention/{offerId}`), so these docs must never be exposed through
 * client-readable security rules.
 */
export interface RetentionOffer extends DatabaseRecord {
  accountId: string;
  /** Email the win-back was sent to (Account.contactEmail ?? Account.email). */
  email?: string;
  /** Entitlement to re-grant on claim (e.g. `basic`). */
  entitlementId: string;
  productIdentifier?: string;
  store?: string;
  /** RC period type at cancel time — `TRIAL` cancellations are the majority. */
  periodType?: string;
  /** When paid/trial access ends (RC `expiration_at_ms`). */
  expirationAtMs?: number;
  /** RC `cancel_reason` (`UNSUBSCRIBE`, `BILLING_ERROR`, …). */
  rcCancelReason?: string;
  /** RC webhook event id that created this offer (dedupe / joins). */
  rcEventId?: string;
  /** RC environment (`PRODUCTION` | `SANDBOX`). */
  environment?: string;
  status: RetentionOfferStatus;
  /** Family's character featured in the win-back email / claim page. */
  characterName?: string;
  /** Hosted image URL for the character (claim page; email may inline a posed sprite). */
  characterImageUrl?: string;
  /** Whether this offer includes the free month (false = survey-only email). */
  includesFreeMonth: boolean;
  /** Days of access granted by the gift code. */
  freeDays: number;
  /**
   * The gift rides the existing wholesaler redemption pipeline: the webhook
   * mints an admin-style free code (`UM-…`) as `Account/{id}/Purchase/{id}`
   * (same shape the admin Giveaway dialog writes) and the claim page sends
   * the parent into the standard `/redeem/{code}` deep-link flow.
   */
  redemptionCode?: string;
  /** Path of the minted gift Purchase doc (`Account/{id}/Purchase/{id}`). */
  purchasePath?: string;
  emailSentAt?: Timestamp;
  /** Set when the gift code is seen redeemed (offer moves to `claimed`). */
  claimedAt?: Timestamp;
  /** Survey answer submitted from the claim page, if any. */
  surveyReason?: CancellationReason;
  surveyMessage?: string;
}

/**
 * Raw RevenueCat webhook event log at `RcEvent/{eventId}` (doc id = RC event
 * id, so replays dedupe via a failed `create()`). Kept for debugging,
 * churn analytics, and joining offers back to their source events.
 */
export interface RcWebhookEventRecord extends DatabaseRecord {
  /** RC event type (`CANCELLATION`, `EXPIRATION`, `RENEWAL`, …). */
  type: string;
  appUserId?: string;
  /** Account resolved from app_user_id / aliases, when found. */
  accountId?: string;
  productId?: string;
  entitlementIds?: string[];
  store?: string;
  environment?: string;
  periodType?: string;
  /** RC `cancel_reason` on CANCELLATION, `expiration_reason` on EXPIRATION. */
  reason?: string;
  expirationAtMs?: number;
  price?: number;
  currency?: string;
  /** What the webhook did with the event (e.g. `winback_email_sent`, `skipped_internal`). */
  actions?: string[];
  /** Full event payload as received. */
  raw: Record<string, unknown>;
}

/** Win-back bookkeeping on the Account doc (backend-written). */
export interface AccountRetentionState {
  /**
   * `Account/{id}/Purchase/{id}` path of the minted free-month gift code.
   * One per account: an unredeemed code is reused on repeat cancellations, a
   * redeemed one means later offers are survey-only. Cleared if the gift is
   * revoked (subscriber uncancelled before redeeming).
   */
  freeMonthPurchasePath?: string;
  freeMonthCodeMintedAt?: Timestamp;
  /** Last win-back email sent (rate-limits repeat cancellations). */
  lastWinbackEmailAt?: Timestamp;
  winbackEmailCount?: number;
}
